import { useTranslation } from 'react-i18next';
import type { ComponentProps } from 'react';

import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';

export const SettingsSection = {
  Wrapper,
  Header,
  Title,
  Description,
  ContentWrapper,
  Footer,
  FooterDescription,
  FooterButton,
  Input: {
    Wrapper: InputWrapper,
    Row: InputRow,
    Label: InputLabel,
    Description: InputDescription,
  },
};

function Wrapper({ className, ...props }: ComponentProps<'section'>) {
  return (
    <section
      className={cn(
        'rounded-lg border border-border bg-card text-card-foreground shadow-sm',
        className,
      )}
      {...props}
    />
  );
}

function Header({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col space-y-1 p-5 pb-0', className)}
      {...props}
    />
  );
}

function Title({ className, ...props }: ComponentProps<'h3'>) {
  return <h3 className={cn('text-base font-semibold', className)} {...props} />;
}

function Description({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      className={cn('text-sm font-normal text-muted-foreground', className)}
      {...props}
    />
  );
}

function ContentWrapper({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div className={cn('border-b border-border p-5', className)} {...props} />
  );
}

/** Always the card's last child: it rounds the card's bottom corners. */
function Footer({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-row items-center justify-between rounded-b-lg bg-muted/50 px-5 py-4',
        className,
      )}
      {...props}
    />
  );
}
function FooterDescription({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      className={cn('text-sm font-normal text-muted-foreground', className)}
      {...props}
    />
  );
}
function FooterButton({
  children,
  className,
  ...props
}: ComponentProps<typeof Button>) {
  const { t } = useTranslation('settings');
  return (
    <Button className={className} {...props}>
      {children || t('Save')}
    </Button>
  );
}

function InputWrapper({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('w-full max-w-[318px]', className)} {...props} />;
}

/**
 * Lays its `Input.Wrapper`s side by side, lifting their single-field cap.
 * Measured against the section rather than the viewport, so the fields fall
 * back to one column whenever the section itself is narrow.
 */
function InputRow({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div className="@container">
      <div
        className={cn(
          'grid grid-cols-1 gap-4 @lg:grid-cols-2 [&>*]:max-w-none',
          className,
        )}
        {...props}
      />
    </div>
  );
}

function InputLabel({ className, ...props }: ComponentProps<'label'>) {
  return (
    <label
      className={cn(
        'mb-2 block text-sm font-medium text-foreground',
        className,
      )}
      {...props}
    />
  );
}

function InputDescription({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      className={cn('mb-2 text-xs text-muted-foreground', className)}
      {...props}
    />
  );
}
